import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleAuthRedirect = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function LoginPage() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = u.role === "admin" ? "/admin" : u.role === "artist" ? "/artist" : u.role === "referrer" ? "/referrer" : "/";
      navigate(dest);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-neutral-300 px-4 py-3 outline-none focus:border-black bg-white text-sm";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 font-sans">
      <div className="hidden lg:block relative bg-neutral-100">
        <img src="https://images.unsplash.com/photo-1605721911519-3dfeb3be25e7?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="flex flex-col px-6 py-10 lg:px-16 lg:py-16">
        <Link to="/" className="overline text-neutral-500 hover:text-black flex items-center gap-2" data-testid="back-home">
          <ArrowLeft size={14} /> d-Pod Gallery
        </Link>
        <div className="my-auto max-w-md w-full mx-auto">
          <h1 className="font-serif text-5xl font-light tracking-tight">{t.auth.login}</h1>
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="login-form">
            <input required type="email" placeholder={t.auth.email} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email" />
            <input required type="password" placeholder={t.auth.password} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password" />
            <div className="text-right -mt-2">
              <Link to="/forgot-password" className="text-xs text-neutral-500 hover:text-black underline" data-testid="forgot-password-link">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <button disabled={loading} type="submit" className="w-full bg-black text-white py-4 overline hover:bg-neutral-800 transition-colors disabled:opacity-50" data-testid="login-submit">
              {loading ? "..." : t.auth.submit}
            </button>
          </form>
          <div className="my-6 flex items-center gap-4 text-neutral-400 overline">
            <div className="flex-1 border-t border-neutral-200" /> {t.auth.orDivider} <div className="flex-1 border-t border-neutral-200" />
          </div>
          <button onClick={googleAuthRedirect} className="w-full border border-neutral-300 py-4 overline hover:bg-neutral-100 transition-colors flex items-center justify-center gap-3" data-testid="google-login">
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C41.4 36.1 44 30.5 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            {t.auth.google}
          </button>
          <p className="mt-8 text-sm text-neutral-500">
            {t.auth.noAccount} <Link to="/register" className="text-black underline" data-testid="go-register">{t.auth.register}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
